import React, { useState } from "react";
import { useDispatch } from "react-redux";

import ProductRowToolTip from "./ProductRowToolTip";
import ProgressBar from "./common/ProgressBar";

import { DEFAULT_AXIS_OPTIONS } from "../../store/data/cartesianData";

const AxisSelectionTableRowSection = ({
  isTopRow,
  isBorder,
  rowSpan,
  product,
  data,
  productLength,
  technicalFeatureLength,
  performanceOverviewLength,
  guidingSystemLength,
  selected,
  mechanics,
  axesToCompare,
  axisSelection,
  steps,
  productTableHeaders,
  technicalFeaturesTableHeaders,
  performanceOverviewTableHeaders,
  getGuidingSystemTableHeaders,
  setCartesianCurrentStep,
  setAxisOptions,
  setAxisSelection,
  addAxisToCompare,
  ...props
}) => {
  const getValueFromPath = (data, valuePath) => {
    if (valuePath.includes("?") && valuePath.includes(":")) {
      // Split the valuePath by the conditional operator
      const [condition, truePath, falsePath] = valuePath.split(/[?:]/);
      console.log("getValueFromPath condition", condition);
      // eslint-disable-next-line no-eval
      const path = eval(condition) ? truePath : falsePath;

      // var f = (str) => {};
      // const path = f(condition) ? truePath : falsePath;

      // let func = new Function(str);
      // const path = func() ? truePath : falsePath;
      return path;
    }
    return valuePath;
  };

  const getValueFromPathFunction = (data, valuePath) => {
    if (typeof valuePath === "function") {
      return valuePath(data);
    }
    return valuePath.split(".").reduce((acc, part) => acc && acc[part], data);
  };

  function getTableContent(style, data, headers, contentLength) {
    const tableContent = headers
      .map((header, index) => {
        if (index >= contentLength) {
          return null; // Skip rendering if index exceeds contentLength
        }
        if (header.subHeaders) {
          // Handle subHeaders by grouping them in a single <td>
          const subHeaderValues = header.subHeaders.map((subHeader) => {
            const value = getValueFromPath(data, subHeader.valuePath)
              ?.split(".")
              ?.reduce((acc, part) => {
                if (part.endsWith("]")) {
                  const index = parseInt(part.slice(0, -1).split("[")[1]);
                  return acc[part.split("[")[0]][index];
                } else {
                  return acc[part];
                }
              }, data);
            if (subHeader.unit && subHeader.unit === "%") {
              return (
                <>
                  {data.axis.functionAvailable !== "support axis" && !isNaN(value) ? (
                    <ProgressBar percentage={value} data-testid={`progress-bar-utilization-${subHeader.name}`} />
                  ) : (
                    "-"
                  )}
                </>
              );
            }
            return value || "-";
          });
          return (
            <td key={header.name} className={style}>
              <div className="flex">
                {subHeaderValues.map((subHeaderValue, subIndex) => (
                  <div key={subIndex} className="flex-1 px-4">
                    {subHeaderValue || "-"}
                  </div>
                ))}
              </div>
            </td>
          );
        }

        // Handle Utilization mechanical drive torque & Utilization Utilization Speed
        if (header.unit && header.unit === "%") {
          const value = header.valuePath.split(".").reduce((acc, part) => acc && acc[part], data);
          return (
            <td className={style}>
              {data.axis.functionAvailable !== "support axis" && !isNaN(value) ? (
                <ProgressBar percentage={value} data-testid={`progress-bar-utilization-${header.name}`} />
              ) : (
                "-"
              )}
            </td>
          );
        }

        // Handle regular headers
        //check if value is a function or a string
        let value = null;
        if (typeof header.valuePath === "function") {
          value = header.valuePath(data);
        } else {
          value = getValueFromPathFunction(data, header.valuePath);
        }
        return (
          <td key={header.name} className={style}>
            {value || "-"}
          </td>
        );
      })
      .filter(Boolean); // Filter out null values

    return tableContent;
  }

  const style = `whitespace-nowrap text-center bg-whitee px-2 py-3 text-sm ${
    isBorder ? "border-b-2 border-gray-200" : ""
  }`;
  const productContent = getTableContent(style, data, productTableHeaders, productLength);
  const technicalFeatureContent = getTableContent(style, data, technicalFeaturesTableHeaders, technicalFeatureLength);
  const performanceOverviewContent = getTableContent(
    style,
    data,
    performanceOverviewTableHeaders,
    performanceOverviewLength
  );
  const guidingSystemContent = getTableContent(
    style,
    data,
    getGuidingSystemTableHeaders(mechanics.quantityCarriages),
    guidingSystemLength
  );

  const dispatch = useDispatch();
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <tr
      className={`AxisSelectionTableRowSection group hover:outline-2 hover:outline-dashed hover:outline-gray-300 text-sm cursor-pointer
        ${selected ? "outline-1 outline-dashed outline-gray-300 bg-gray-100" : ""}
      `}
      onClick={() => {
        console.log("click on row", "product: ", product, "data :", data);
        const axis = { ...data, product };
        if (axisSelection.selectedAxis && axis.id === axisSelection.selectedAxis.id) {
          dispatch(setCartesianCurrentStep(steps.AXIS_SELECTION));
          dispatch(setAxisOptions(DEFAULT_AXIS_OPTIONS));
          dispatch(setAxisSelection({ ...axisSelection, selectedAxis: null, isValid: false }));
        }
        dispatch(addAxisToCompare({ axis }));
      }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      {...props}
    >
      {isTopRow && (
        <td rowSpan={rowSpan} className="border-b-2 border-l-2 border-gray-200 bg-gray-100 sticky left-0 px-1">
          <div className="flex justify-center items-center space-x-1">
            <div className="text-center font-semibold w-min">{product}</div>
            <ProductRowToolTip
              description="temp"
              data={data}
              showTooltip={showTooltip}
              data-testid="product-row-tool-tip"
            />
          </div>
        </td>
      )}
      {productContent}
      {technicalFeatureContent}
      {performanceOverviewContent}
      {guidingSystemContent}
      <td className={`sticky right-0 !bg-gray-100 ${style} `}>
        <input
          type="checkbox"
          className="bg-white checked:bg-secondary cursor-pointer rounded-sm border-gray-400 checked:border-secondary"
          readOnly
          checked={axesToCompare?.some((axis) => axis.id === data.id)}
        />
      </td>
    </tr>
  );
};

export default AxisSelectionTableRowSection;
